import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, CheckCircle2, ClipboardCheck, Package, FileDown,
  AlertTriangle, Info, Check, Search, X, XCircle, Ban
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { Dialog } from '@components/Dialog';
import { inventorySessionService } from '../../services/inventorySessionService';
import {
  InventorySessionDetail,
  InventoryEntry,
  InventoryProviderGroup,
} from '../../types/inventorySession';
import { MATERIAL_TYPE_OPTIONS } from '../../types/rawMaterial';
import { clsx } from 'clsx';

// ─── Helpers ──────────────────────────────────────────────────────

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatQty = (value?: number | null) => {
  if (value === undefined || value === null) return '—';
  return value.toLocaleString('pt-BR');
};

/** Código exibido do item: SKU do produto ou código da matéria-prima */
const entryCode = (entry: InventoryEntry): string =>
  entry.item_type === 'raw_material'
    ? entry.raw_material_code || '—'
    : entry.product_sku || '—';

/** Unidade do item (apenas para matérias-primas) */
const entryUnit = (entry: InventoryEntry): string | null =>
  entry.item_type === 'raw_material' ? entry.raw_material_unit : null;

/** Quantidade formatada com unidade quando o entry é de matéria-prima */
const formatQtyWithUnit = (value: number | null | undefined, entry: InventoryEntry) => {
  const qty = formatQty(value);
  const unit = entryUnit(entry);
  return unit && qty !== '—' ? `${qty} ${unit}` : qty;
};

const getMaterialTypeLabel = (materialType: number | null): string => {
  const option = MATERIAL_TYPE_OPTIONS.find(o => o.value === materialType);
  return option ? option.label : '—';
};

// ─── Confer Modal ─────────────────────────────────────────────────

interface ConferModalProps {
  isOpen: boolean;
  entry: InventoryEntry | null;
  sessionTitle: string;
  onClose: () => void;
  onConfer: (entryId: number, createMovement: boolean, newBalance?: number) => Promise<void>;
  saving: boolean;
}

const ConferModal: React.FC<ConferModalProps> = ({
  isOpen, entry, sessionTitle, onClose, onConfer, saving,
}) => {
  const [createMovement, setCreateMovement] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [error, setError] = useState('');

  // Reset state when entry changes
  useEffect(() => {
    if (entry) {
      setCreateMovement(false);
      setNewBalance('');
      setError('');
    }
  }, [entry]);

  const handleSubmit = async () => {
    if (!entry) return;
    const isRawMaterial = entry.item_type === 'raw_material';
    try {
      if (createMovement) {
        // Matérias-primas podem ter unidades fracionárias (ex: KG, M)
        const balance = isRawMaterial
          ? parseFloat(newBalance.replace(',', '.'))
          : parseInt(newBalance, 10);
        if (isNaN(balance) || balance < 0) {
          setError(
            isRawMaterial
              ? 'Informe um saldo válido (número >= 0)'
              : 'Informe um saldo válido (número inteiro >= 0)'
          );
          return;
        }
        await onConfer(entry.inventory_entries_id, true, balance);
      } else {
        await onConfer(entry.inventory_entries_id, false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao registrar conferência');
    }
  };

  if (!entry) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title="Conferência de Inventário"
      size="md"
      closeOnBackdrop={!saving}
      showCloseButton={!saving}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            <Check size={16} className="mr-1" /> Confirmar Conferência
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Item info */}
        <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
          <Package size={18} className="text-emerald-600" />
          <div>
            <p className="font-medium text-sm text-slate-900">
              {entryCode(entry)} — {entry.product_description || 'Sem descrição'}
            </p>
            {entry.item_type === 'raw_material' && (
              <p className="text-xs text-slate-500">
                Unidade: <strong>{entry.raw_material_unit || '—'}</strong>
                {' · '}
                Tipo: <strong>{getMaterialTypeLabel(entry.material_type)}</strong>
              </p>
            )}
            <p className="text-xs text-slate-500">
              Estoque atual no sistema:{' '}
              <strong>{formatQtyWithUnit(entry.current_stock_quantity, entry)}</strong>
            </p>
          </div>
        </div>

        {/* Choice: stock is correct or needs adjustment */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            O estoque físico está compatível com o sistema?
          </label>
          <div className="space-y-2">
            <label className={clsx(
              'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              !createMovement ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
            )}>
              <input
                type="radio"
                name="movement_choice"
                checked={!createMovement}
                onChange={() => setCreateMovement(false)}
                className="text-emerald-600 focus:ring-emerald-500"
                disabled={saving}
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Sim, o estoque está correto</p>
                <p className="text-xs text-slate-500">
                  Apenas marca como conferido, sem gerar lançamento de estoque
                </p>
              </div>
            </label>
            <label className={clsx(
              'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              createMovement ? 'border-amber-300 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'
            )}>
              <input
                type="radio"
                name="movement_choice"
                checked={createMovement}
                onChange={() => setCreateMovement(true)}
                className="text-amber-600 focus:ring-amber-500"
                disabled={saving}
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Não, precisa de ajuste de saldo</p>
                <p className="text-xs text-slate-500">
                  Gera um lançamento de estoque (Balanço/Inventário — Saldo) para ajustar o valor
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* New balance input (only when creating movement) */}
        {createMovement && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Saldo Atual (quantidade física real
              {entry.item_type === 'raw_material' && entry.raw_material_unit
                ? ` em ${entry.raw_material_unit}`
                : ''}
              ) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step={entry.item_type === 'raw_material' ? 'any' : '1'}
              value={newBalance}
              onChange={(e) => { setNewBalance(e.target.value); setError(''); }}
              className={clsx(
                'w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
                error ? 'border-red-300' : 'border-slate-300'
              )}
              disabled={saving}
              placeholder="Informe o saldo real do estoque físico"
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            <p className="text-xs text-slate-400 mt-1">
              Este valor será o novo saldo do produto no sistema
            </p>
          </div>
        )}

        {/* Business rules info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          <p className="font-medium mb-1">ℹ️ Regras:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Tipo de Origem: <strong>Balanço/Inventário</strong></li>
            <li>Tipo de Movimentação: <strong>Saldo</strong></li>
            <li>Observação: <em>Saldo da conferência de inventário: {sessionTitle}</em></li>
            {createMovement && (
              <li className="text-amber-700">O saldo do produto será atualizado para o valor informado</li>
            )}
          </ul>
        </div>
      </div>
    </Dialog>
  );
};

// ─── Provider Tab Content ─────────────────────────────────────────

interface ProviderTabContentProps {
  group: InventoryProviderGroup;
  isActive: boolean;
  onConfer: (entry: InventoryEntry) => void;
}

const ProviderTabContent: React.FC<ProviderTabContentProps> = ({
  group, isActive, onConfer,
}) => {
  const [search, setSearch] = useState('');

  const matchesSearch = (entry: InventoryEntry) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (entry.product_sku || '').toLowerCase().includes(q) ||
      (entry.raw_material_code || '').toLowerCase().includes(q) ||
      (entry.product_description || '').toLowerCase().includes(q)
    );
  };

  const pendingEntries = group.entries.filter(e => !e.conferred_at);
  const conferredEntries = group.entries.filter(e => !!e.conferred_at);

  const sortByStock = (a: InventoryEntry, b: InventoryEntry) => {
    const aStock = a.current_stock_quantity ?? 0;
    const bStock = b.current_stock_quantity ?? 0;
    if (aStock > 0 && bStock <= 0) return -1;
    if (aStock <= 0 && bStock > 0) return 1;
    return entryCode(a).localeCompare(entryCode(b));
  };

  const sortedPending = useMemo(
    () => [...pendingEntries].sort(sortByStock).filter(matchesSearch),
    [pendingEntries, search]
  );
  const sortedConferred = useMemo(
    () => [...conferredEntries]
      .sort((a, b) => entryCode(a).localeCompare(entryCode(b)))
      .filter(matchesSearch),
    [conferredEntries, search]
  );

  const totalFiltered = sortedPending.length + sortedConferred.length;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Pesquisar por SKU ou nome..."
          className="w-full pl-9 pr-9 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {search && (
        <p className="text-xs text-slate-500">
          {totalFiltered === 0 ? 'Nenhum produto encontrado' : `${totalFiltered} produto(s) encontrado(s)`}
        </p>
      )}

      {/* Pending entries */}
      {sortedPending.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-500" />
            Pendentes de conferência ({sortedPending.length})
          </h4>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {sortedPending.map(entry => (
              <div
                key={entry.inventory_entries_id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors"
              >
                {/* Código + descrição — grows to fill available space */}
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-xs font-semibold text-slate-700 mr-1.5">
                    {entryCode(entry)}
                  </span>
                  <span className="text-xs text-slate-600 break-words">
                    {entry.product_description || '—'}
                  </span>
                  {entryUnit(entry) && (
                    <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-500">
                      {entryUnit(entry)}
                    </span>
                  )}
                </div>
                {/* Stock badge */}
                <div className="flex-shrink-0 text-right">
                  <span className="text-xs text-slate-500">Estoque:</span>{' '}
                  <span className={clsx(
                    'text-xs font-semibold',
                    (entry.current_stock_quantity ?? 0) > 0 ? 'text-slate-900' : 'text-red-500'
                  )}>
                    {formatQtyWithUnit(entry.current_stock_quantity, entry)}
                  </span>
                </div>
                {/* Conferir button */}
                {isActive && (
                  <PermissionGuard permission="stock_movements.create">
                    <Button
                      size="sm"
                      variant="outline"
                      icon={<ClipboardCheck size={14} />}
                      onClick={() => onConfer(entry)}
                    >
                      Conferir
                    </Button>
                  </PermissionGuard>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conferred entries */}
      {sortedConferred.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald-500" />
            Produtos conferidos ({sortedConferred.length})
          </h4>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {sortedConferred.map(entry => (
              <div
                key={entry.inventory_entries_id}
                className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2.5 bg-white"
              >
                {/* Código + descrição */}
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-xs font-semibold text-slate-700 mr-1.5">
                    {entryCode(entry)}
                  </span>
                  <span className="text-xs text-slate-600 break-words">
                    {entry.product_description || '—'}
                  </span>
                  {entryUnit(entry) && (
                    <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-500">
                      {entryUnit(entry)}
                    </span>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-400">
                    <span>{formatDate(entry.conferred_at)}</span>
                    {entry.conferred_by && <span>{entry.conferred_by}</span>}
                  </div>
                </div>
                {/* Right side: stock info + badge */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0 text-right">
                  <div className="text-xs text-slate-500">
                    Estoque: <span className="font-medium text-slate-700">{formatQtyWithUnit(entry.current_stock_quantity, entry)}</span>
                    {entry.stock_movement_id && (
                      <> → <span className="font-semibold text-slate-900">{formatQtyWithUnit(entry.stock_quantity, entry)}</span></>
                    )}
                  </div>
                  {entry.stock_movement_id ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-amber-100 text-amber-700 border-amber-200">
                      Saldo ajustado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200">
                      Estoque OK
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sortedPending.length === 0 && sortedConferred.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-slate-500">
            {search ? 'Nenhum produto corresponde à pesquisa' : 'Nenhum produto neste fornecedor'}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Finalized Adjustments Report ─────────────────────────────────

interface AdjustmentsReportProps {
  detail: InventorySessionDetail;
}

const AdjustmentsReport: React.FC<AdjustmentsReportProps> = ({ detail }) => {
  const adjustedProviders = detail.providers
    .map(pg => ({
      ...pg,
      entries: pg.entries.filter(e => e.stock_movement_id != null),
    }))
    .filter(pg => pg.entries.length > 0);

  if (adjustedProviders.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
        <CheckCircle2 size={24} className="mx-auto text-emerald-500 mb-2" />
        <p className="text-sm text-emerald-700 font-medium">Nenhum ajuste de saldo foi necessário neste inventário</p>
        <p className="text-xs text-emerald-600 mt-1">Todos os produtos estavam com o estoque correto</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <AlertTriangle size={18} className="text-amber-500" />
        Relatório de Ajustes de Saldo
      </h3>
      {adjustedProviders.map(pg => (
        <Card key={pg.provider_id}>
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-700">
              {pg.provider_trade_name || pg.provider_legal_name}
            </h4>
          </div>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {pg.entries.map(entry => {
                const diff = (entry.new_balance ?? 0) - (entry.previous_balance ?? 0);
                return (
                  <div
                    key={entry.inventory_entries_id}
                    className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2.5"
                  >
                    {/* Código + descrição */}
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-xs font-semibold text-slate-700 mr-1.5">
                        {entryCode(entry)}
                      </span>
                      <span className="text-xs text-slate-600 break-words">
                        {entry.product_description || '—'}
                      </span>
                      {entryUnit(entry) && (
                        <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-500">
                          {entryUnit(entry)}
                        </span>
                      )}
                      <div className="text-xs text-slate-400 mt-0.5">{formatDate(entry.conferred_at)}{entry.conferred_by ? ` — ${entry.conferred_by}` : ''}</div>
                    </div>
                    {/* Balances + diff */}
                    <div className="flex-shrink-0 text-right text-xs">
                      <div className="text-slate-500">
                        <span>{formatQty(entry.previous_balance)}</span>
                        <span className="mx-1 text-slate-400">→</span>
                        <span className="font-semibold text-slate-900">{formatQtyWithUnit(entry.new_balance, entry)}</span>
                      </div>
                      <span className={clsx(
                        'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mt-1',
                        diff > 0 ? 'bg-emerald-100 text-emerald-700' : diff < 0 ? 'bg-red-100 text-red-700' : 'text-slate-400'
                      )}>
                        {diff > 0 ? '+' : ''}{diff}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─── Unconfirmed Items Report ─────────────────────────────────────

interface UnconferredReportProps {
  detail: InventorySessionDetail;
}

const UnconferredReport: React.FC<UnconferredReportProps> = ({ detail }) => {
  const unconferredProviders = detail.providers
    .map(pg => ({
      ...pg,
      entries: pg.entries.filter(e => !e.conferred_at),
    }))
    .filter(pg => pg.entries.length > 0);

  if (unconferredProviders.length === 0) {
    return null;
  }

  const totalUnconferred = unconferredProviders.reduce((acc, pg) => acc + pg.entries.length, 0);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <Ban size={18} className="text-red-500" />
        Produtos Não Conferidos ({totalUnconferred})
      </h3>
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
        <p>Os produtos abaixo não foram conferidos durante esta sessão de inventário.</p>
      </div>
      {unconferredProviders.map(pg => (
        <Card key={pg.provider_id}>
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-700">
              {pg.provider_trade_name || pg.provider_legal_name}
              <span className="ml-2 text-xs font-normal text-slate-500">
                ({pg.entries.length} {pg.entries.length === 1 ? 'produto' : 'produtos'})
              </span>
            </h4>
          </div>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {pg.entries.map(entry => (
                <div
                  key={entry.inventory_entries_id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs font-semibold text-slate-700 mr-1.5">
                      {entryCode(entry)}
                    </span>
                    <span className="text-xs text-slate-600 break-words">
                      {entry.product_description || '—'}
                    </span>
                    {entryUnit(entry) && (
                      <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-500">
                        {entryUnit(entry)}
                      </span>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right text-xs">
                    <span className="text-slate-500">Estoque sistema:</span>{' '}
                    <span className="font-medium text-slate-700">{formatQtyWithUnit(entry.current_stock_quantity, entry)}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-red-100 text-red-700 border-red-200">
                    Não conferido
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────

export const InventorySessionDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = parseInt(id || '0', 10);

  const [detail, setDetail] = useState<InventorySessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Provider tabs
  const [activeProviderTab, setActiveProviderTab] = useState(0);

  // Confer modal
  const [conferModalOpen, setConferModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<InventoryEntry | null>(null);
  const [conferring, setConferring] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // PDF
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Close session
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closingSession, setClosingSession] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await inventorySessionService.getSessionDetail(sessionId);
      setDetail(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar inventário');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleOpenConfer = (entry: InventoryEntry) => {
    setSelectedEntry(entry);
    setConferModalOpen(true);
  };

  const handleConfer = async (entryId: number, createMovement: boolean, newBalance?: number) => {
    setConferring(true);
    try {
      const updatedDetail = await inventorySessionService.conferEntry(sessionId, {
        inventory_entries_id: entryId,
        create_stock_movement: createMovement,
        new_balance: newBalance,
      });
      setDetail(updatedDetail);
      setConferModalOpen(false);
      setSelectedEntry(null);
      const msg = createMovement
        ? 'Produto conferido e saldo ajustado com sucesso!'
        : 'Produto conferido com sucesso!';
      setSuccessMessage(msg);
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      throw err; // Propagate to ConferModal for display
    } finally {
      setConferring(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await inventorySessionService.downloadReportPdf(sessionId);
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCloseSession = async () => {
    setClosingSession(true);
    try {
      const updatedDetail = await inventorySessionService.closeSession(sessionId);
      setDetail(updatedDetail);
      setCloseDialogOpen(false);
      setSuccessMessage('Sessão de inventário encerrada com sucesso!');
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao encerrar sessão');
    } finally {
      setClosingSession(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-emerald-500" />
        <span className="ml-3 text-slate-500">Carregando inventário...</span>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" icon={<ArrowLeft size={16} />} onClick={() => navigate('/stock-movements')}>
          Voltar
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Inventário não encontrado'}
        </div>
      </div>
    );
  }

  const { session, providers } = detail;
  const progress = session.total_entries > 0
    ? Math.round((session.conferred_entries / session.total_entries) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-3">
          <Button variant="outline" size="sm" icon={<ArrowLeft size={16} />} onClick={() => navigate('/stock-movements')}>
            Voltar
          </Button>
          <div>
            <h1 className="text-base sm:text-h1 font-semibold">{session.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <p className="hidden sm:block text-sm text-slate-500">
                Criado por {session.created_by || '—'} em {formatDate(session.created_at)}
              </p>
              {session.is_active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-blue-100 text-blue-700 border-blue-200">
                  <ClipboardCheck size={12} /> Em andamento
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200">
                  <CheckCircle2 size={12} /> Finalizado
                </span>
              )}
            </div>
          </div>
        </div>
        {!session.is_active && (
          <PermissionGuard permission="stock_movements.read">
            <Button
              variant="outline"
              icon={<FileDown size={16} />}
              onClick={handleDownloadPdf}
              loading={downloadingPdf}
            >
              Baixar PDF
            </Button>
          </PermissionGuard>
        )}
        {session.is_active && (
          <PermissionGuard permission="stock_movements.create">
            <Button
              variant="danger"
              icon={<XCircle size={16} />}
              onClick={() => setCloseDialogOpen(true)}
            >
              Encerrar Conferência
            </Button>
          </PermissionGuard>
        )}
      </div>

      {/* Progress bar */}
      <Card>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600 font-medium">Progresso da conferência</span>
                <span className="text-slate-500">
                  {session.conferred_entries} de {session.total_entries} produtos ({progress}%)
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div
                  className={clsx(
                    'h-3 rounded-full transition-all duration-500',
                    progress === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Info size={18} /> {successMessage}
        </div>
      )}

      {/* Adjustments report for finalized sessions */}
      {!session.is_active && (
        <AdjustmentsReport detail={detail} />
      )}

      {/* Unconferred items report for finalized sessions */}
      {!session.is_active && (
        <UnconferredReport detail={detail} />
      )}

      {/* Provider tabs */}
      {providers.length > 0 && (
        <Card>
          <div className="border-b border-slate-200 overflow-x-auto">
            <nav className="flex -mb-px gap-0" aria-label="Abas de fornecedores">
              {providers.map((pg, idx) => {
                const pendingCount = pg.entries.filter(e => !e.conferred_at).length;
                const totalCount = pg.entries.length;
                return (
                  <button
                    key={pg.provider_id}
                    onClick={() => setActiveProviderTab(idx)}
                    className={clsx(
                      'inline-flex items-center gap-2 whitespace-nowrap py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                      activeProviderTab === idx
                        ? 'border-emerald-500 text-emerald-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                    )}
                  >
                    <Package size={14} />
                    <span className="truncate max-w-[150px]">
                      {pg.provider_trade_name || pg.provider_legal_name}
                    </span>
                    <span className={clsx(
                      'text-xs px-1.5 py-0.5 rounded-full',
                      pendingCount === 0
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    )}>
                      {totalCount - pendingCount}/{totalCount}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
          <CardContent>
            {providers[activeProviderTab] && (
              <ProviderTabContent
                group={providers[activeProviderTab]}
                isActive={session.is_active}
                onConfer={handleOpenConfer}
              />
            )}
          </CardContent>
        </Card>
      )}

      {providers.length === 0 && !loading && (
        <Card>
          <CardContent>
            <div className="text-center py-12">
              <div className="text-5xl text-slate-200 mb-3">📦</div>
              <h3 className="text-base font-medium text-slate-700 mb-1">Nenhum produto encontrado</h3>
              <p className="text-sm text-slate-500">Não há produtos ativos vinculados a fornecedores</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Close Session Confirmation Dialog */}
      <Dialog
        isOpen={closeDialogOpen}
        onClose={() => !closingSession && setCloseDialogOpen(false)}
        title="Encerrar Conferência de Inventário"
        size="md"
        closeOnBackdrop={!closingSession}
        showCloseButton={!closingSession}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)} disabled={closingSession}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleCloseSession} loading={closingSession}>
              <XCircle size={16} className="mr-1" /> Confirmar Encerramento
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Atenção: Nem todos os produtos foram conferidos!</p>
                <p>
                  {session.conferred_entries} de {session.total_entries} produtos foram conferidos
                  ({session.total_entries - session.conferred_entries} pendentes).
                </p>
              </div>
            </div>
          </div>
          <div className="text-sm text-slate-600 space-y-2">
            <p>Ao encerrar a conferência:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-500">
              <li>A sessão será marcada como <strong>inativa/finalizada</strong></li>
              <li>Não será mais possível conferir produtos pendentes</li>
              <li>O relatório mostrará os itens conferidos e não conferidos</li>
            </ul>
          </div>
        </div>
      </Dialog>

      {/* Confer Modal */}
      <ConferModal
        isOpen={conferModalOpen}
        entry={selectedEntry}
        sessionTitle={session.title}
        onClose={() => { setConferModalOpen(false); setSelectedEntry(null); }}
        onConfer={handleConfer}
        saving={conferring}
      />
    </div>
  );
};
